import React, { useEffect, useState } from 'react';

import Page from './Page';

import { getPlan, populate as populatePlan } from '@go-client/lib/api/plan';
import { Plan } from '@go-shared/types/plan';
import { getProperty } from '@go-client/lib/api/property';
import { populate as populateProperty } from '@go-shared/lib/property';
import { getBrand } from '@go-client/lib/api/brand';
import { ReportsGrid } from '@go-client/components/ReportsGrid/ReportsGrid';
import {
  populate as populateEventTemplate,
  getPropertyEventTemplates,
} from '@go-client/lib/api/eventTemplate';
import {
  getVenueTemplatesByPropertyId,
  populate as populateVenueTemplate,
} from '@go-client/lib/api/venueTemplate';
import {
  getStaffTemplatesByPropertyId,
  populate as populateStaffTemplate,
} from '@go-client/lib/api/staffTemplate';
import { populate as populateEvent } from '@go-client/lib/event';
import { Event } from '@go-shared/types/event';
import { HourFormat } from '@go-shared/types/brand';
import { RouteComponentProps } from 'react-router-dom';
import { getSailing } from '@go-client/lib/api/itinerary';
import { BareSailingDay } from '@go-shared/types/sailing';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';

import './ReportsDashboard.scss';
import { Button, InlineIcon } from '@spark-labs/avocado-ui';
import { LoadingIndicator } from '@spark-labs/avocado-ui/dist/components/LoadingIndicator';
import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { MenuBar } from '@go-client/components';

export interface GridRequiredData {
  events: Event[];
  plan: Plan;
  hourFormat: HourFormat;
  sailingDay: BareSailingDay;
}

type ReportsDashboardProps = RouteComponentProps<{
  sailingDay: string;
  sailingId: string;
}>;

const ReportsDashboard = (props: ReportsDashboardProps) => {
  const [gridRequiredData, setGridRequiredData] = useState<GridRequiredData | undefined>(undefined);
  useEffect(() => {
    getDashboardData(props)
      .then(data => setGridRequiredData(data))
      .catch(console.error);
  }, []);
  return (
    <Page className="ReportsDashboard absolute flex items-stretch overflow-hidden">
      <MenuBar
        {...props}
        property={gridRequiredData ? gridRequiredData.plan.property : undefined}
      />
      <div className="SideMenu bg-blue-darkest flex flex-col">
        <BrandLogo
          logo={gridRequiredData ? gridRequiredData.plan.property.brand.logo : undefined}
        />
        <PropertyBanner
          {...props}
          property={gridRequiredData ? gridRequiredData.plan.property : undefined}
        />
        <div className="py-4 mx-auto">
          <Button onClick={() => props.history.goBack()}>
            <InlineIcon name={'calendar'} className="text-base" />
            <span className="ml-1">Grid view</span>
          </Button>
        </div>
      </div>
      <div className="w-full h-full">
        {gridRequiredData ? (
          <div className="align-start h-full">
            <ReportsGrid
              sailingDay={gridRequiredData.sailingDay}
              hourFormat={gridRequiredData.hourFormat}
              events={gridRequiredData.events}
              shipStartTime={gridRequiredData.plan.dayStart}
            />
          </div>
        ) : (
          <LoadingIndicator className="h-full" />
        )}
      </div>
    </Page>
  );
};

const getDashboardData = async ({ match }: ReportsDashboardProps): Promise<GridRequiredData> => {
  let bareSailing = await getSailing(match.params.sailingId);
  let bareSailingDay = bareSailing.days.find(d => d._id === match.params.sailingDay);

  let barePlan = await getPlan(bareSailingDay!.plan);
  let bareProperty = await getProperty(barePlan.property);
  let brand = await getBrand(bareProperty.brand);

  let property = populateProperty(bareProperty, brand);
  let plan = populatePlan(barePlan, property);

  let [bareEventTemplates, bareVenueTemplates, bareStaffTemplates] = await Promise.all([
    getPropertyEventTemplates(plan.property._id),
    getVenueTemplatesByPropertyId(plan.property._id),
    getStaffTemplatesByPropertyId(plan.property._id),
  ]);

  let eventTemplates = bareEventTemplates.map(et => populateEventTemplate(et, plan.property.brand));

  let venueTemplates = bareVenueTemplates.map(vt => populateVenueTemplate(vt, plan.property));

  let staffTemplates = bareStaffTemplates.map(st => populateStaffTemplate(st, plan.property));

  let events = plan.events.map(bareEvent =>
    populateEvent(bareEvent, eventTemplates, venueTemplates, staffTemplates, plan.day)
  );

  return {
    events,
    plan,
    hourFormat: brand.hourFormat,
    sailingDay: bareSailingDay!,
  };
};

export default ReportsDashboard;
